import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
import {
  ContactModel,
  ContactOnServerUpdated,
  ContactRelationshipModel,
  ContactRelationshipOnServerCreated,
  ContactRelationshipsService,
  ContactsDataSource,
  ContactsPageRequested,
  ContactsService,
  OneContactOnServerDeleted,
  selectContactErrorState,
  selectContactsActionLoading,
  ViewContext,
} from '../../../../../../core/lists';
import { TabletUtil } from '../../../../../../core/utils';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/auth/_services';
import { Update } from '@ngrx/entity';
import { FilteredViewContactRelationDialogComponent } from 'src/app/views/partials/content/general/filtered-view-contact-relation-dialog/filtered-view-contact-relation-dialog.component';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CreateFilteredViewDialogComponent } from '../../../../filtered-view/create-filtered-view-dialog/create-filtered-view-dialog.component';
import { GlobalSettingsService } from '../../../../../../core/services/global-settings.service';
import { FilteredViewService } from '../../../../../../core/services/filtered-view.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { utc } from 'moment';
import { ZoomInEntityComponent } from '../../../../../partials/content/general/zoom-in-entity/zoom-in-entity.component';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { DownloadService } from '../../../../../../shared/utils/download.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-contacts-list',
  templateUrl: './contacts-list.component.html',
  styleUrls: ['./contacts-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ContactsListComponent implements OnInit, OnDestroy {
  dataSource: ContactsDataSource;
  displayedColumns = [
    'id',
    'profile',
    'name',
    'username',
    'cellPhone',
    'entityType',
    'isActive',
    'actions',
  ];
  searchBarColumns = [
    'searchId',
    'searchProfile',
    'searchNameEmail',
    'searchUsername',
    'searchCellPhone',
    'searchEntityType',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @Input() filteredView;
  @Input() filterEntities;
  @Output() totalCount = new EventEmitter<any>();
  @Output() relatedIds = new EventEmitter<any>();
  @Input() allRelated;
  searchIdInput = '';
  searchNameEmailInput = '';
  searchUsernameInput = '';
  searchCellPhoneInput = '';
  searchEntityTypeSelect = [];
  searchIsActiveSelect = 'true';
  statusTracker = 0;
  sortRelation: { label: string; type: 'ASC' | 'DESC' } = { label: '', type: 'DESC' };
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  entity_types = [
    { name: 'Client', value: 'CLIENT' },
    { name: 'Doctor', value: 'DOCTOR' },
    { name: 'Patient', value: 'PATIENT' },
    { name: 'Facility', value: 'FACILITY' },
  ];
  loading = false;
  isFilteredView = false;
  phone = '';
  isInbound = false;
  callid = '';
  expandedElement: any;
  list = [];
  selectedContact: any;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize;

  constructor(
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private tabletUtil: TabletUtil,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    public globalSettings: GlobalSettingsService,
    private translate: TranslateService,
    private sessionService: FilteredViewService,
    private authService: AuthService,
    public stringUtils: StringUtilsService,
    private contactsService: ContactsService,
    private permissionsService: NgxPermissionsService,
    private contactRelationshipsService: ContactRelationshipsService,
    private navigationService: NavigationService,
    private downloadService: DownloadService,
  ) {
    if (this.router.url.includes('/inbound')) {
      this.isInbound = true;
    }
  }

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this 3 lines
    this.filtersApplied = this.navigationService.getFilters();
    if (this.filtersApplied) this.firstLoadAfterBack = true;
    this.pageSize = this.filtersApplied?.pageSize;

    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'name';
      this.sort.direction = this.sort.direction || 'asc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    /* Data load will be triggered in two cases:
    - when a pagination event occurs => this.paginator.page
    - when a sort event occurs => this.sort.sortChange
    **/
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadContactsList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadContactsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });
    // Init DataSource
    this.dataSource = new ContactsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.relatedIds.emit(res);
      });
    this.subscriptions.push({ id: 4, sub: entitiesSubscription });
    // Read from URL itemId, for restore previous state
    const routeSubscription = this.activatedRoute.queryParams.subscribe((params) => {
      if (params.callerid) {
        this.phone = params.callerid;
      }
      if (params.callid) {
        this.callid = params.callid;
      }
      this.loadContactsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
    this.dataSource.paginatorTotal$.subscribe((res) => {
      this.totalCount.emit(res);
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadContactsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'name',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    // Call request from server
    this.store.dispatch(new ContactsPageRequested({ page: queryParams }));
  }

  filterConfigurationR(order, direction) {
    const filter: any = {};
    const o = order && order !== '' ? order : 'asc';
    if (direction === '' || direction === 'name') {
      filter.sort = `entity.lastName,${o}`;
    }
    return filter;
  }

  filterConfiguration(reset_applied_flag?): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    filter.related = this.filterEntities || [];
    if (this.isInbound) {
      filter.isInbound = this.isInbound;

      if (this.phone) filter.callerId = this.phone;
    }
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.searchEntityTypeSelect = appliedFilters.entityTypes;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchIdInput = appliedFilters.id;
        this.searchNameEmailInput = appliedFilters.nameEmail;
        this.searchUsernameInput = appliedFilters.username;
        this.searchCellPhoneInput = appliedFilters.cellPhone?.replace(/[()\-\s]/g, '');
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'asc';
      this.sort.active = this.filtersApplied?.sortActive || 'name';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.id = this.searchIdInput;
      filter.nameEmail = this.searchNameEmailInput;
      filter.username = this.searchUsernameInput;
      filter.cellPhone = this.searchCellPhoneInput?.replace(/[()\-\s]/g, '');
      filter.entityTypes = this.searchEntityTypeSelect || [];
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'asc';
    filter.sortActive = this.sort.active || 'name';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  openFilteredViewDialog(val) {
    this.isFilteredView = this.globalSettings.isFilteredView();
    const dialogRef = this.dialog.open(CreateFilteredViewDialogComponent, {
      width: '800px',
      data: {
        sessionOpened: this.isFilteredView,
        entity: val,
        callid: this.callid,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      setTimeout(() => {
        this.isFilteredView = this.globalSettings.isFilteredView();
        this.cdr.detectChanges();
      }, 30);
    });
  }

  deleteContact(_item: ContactModel) {
    const _title: string = this.translate.instant('CONTACT.DELETE_CONTACT_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'CONTACT.DELETE_CONTACT_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'CONTACT.DELETE_CONTACT_SIMPLE.WAIT_DESCRIPTION',
    );
    const _deleteMessage = this.translate.instant('CONTACT.DELETE_CONTACT_SIMPLE.MESSAGE');

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }

      this.store.dispatch(new OneContactOnServerDeleted({ id: _item.id }));
      const actionLoadingSubscription = this.store
        .pipe(select(selectContactsActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectContactErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error && error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            if (this.subscriptions.find((sub) => sub.id === 9) === undefined) {
              this.subscriptions.push({ id: 9, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.error);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Delete,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _deleteMessage,
                MessageType.Delete,
                3000,
                false,
                false,
              );
              this.loadContactsList();
            }
          }
          this.cdr.detectChanges();
        });
      if (this.subscriptions.find((sub) => sub.id === 8) === undefined) {
        this.subscriptions.push({ id: 8, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 7) === undefined) {
      this.subscriptions.push({ id: 7, sub: dialogRefSubscription });
    }
  }

  updateStatusForContact(contact: ContactModel) {
    const _title: string = this.translate.instant('CONTACT.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant('CONTACT.UPDATE_STATUS_SIMPLE.DESCRIPTION');
    const _waitDesciption: string = this.translate.instant(
      'CONTACT.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );
    const _updateStatusSingleMessage = this.translate.instant(
      'CONTACT.UPDATE_STATUS_SIMPLE.MESSAGE',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newContact: ContactModel = Object.assign(new ContactModel(), contact);
      newContact.changeActiveStatus();
      const accessObj = {
        entity_id: newContact.id,
        access_from: 'Contact List',
        entity_type: 'CONTACT',
      };
      this.sessionService.createRecentSession(accessObj).subscribe();
      const updateContact: Update<ContactModel> = {
        id: newContact.id,
        changes: newContact,
      };
      this.store.dispatch(
        new ContactOnServerUpdated({
          partialContact: updateContact,
          contact: newContact,
        }),
      );
      const actionLoadingSubscription = this.store
        .pipe(select(selectContactsActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectContactErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error && error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            if (this.subscriptions.find((sub) => sub.id === 15) === undefined) {
              this.subscriptions.push({ id: 15, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.error);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Update,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _updateStatusSingleMessage,
                MessageType.Update,
                3000,
                false,
                false,
              );
            }
          }
        });
      if (this.subscriptions.find((sub) => sub.id === 14) === undefined) {
        this.subscriptions.push({ id: 14, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 13) === undefined) {
      this.subscriptions.push({ id: 13, sub: dialogRefSubscription });
    }
  }

  canViewContact(): boolean {
    return !!this.permissionsService.getPermission('canViewContact');
  }

  canEditContact(): boolean {
    return !!this.permissionsService.getPermission('canEditContact');
  }

  viewContact(id) {
    if (!this.canViewContact()) return;
    if (this.canEditContact()) {
      this.editContact(id);
      return;
    }
    const queryParams = { viewContext: ViewContext.contacts };
    this.navigationService.navigateTo('lists/contacts/view/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewContactDblclick(ref: string, contact: ContactModel): void {
    this.tabletUtil.handleDblclick(ref, this.viewContact.bind(this, contact.id));
  }

  editContact(id) {
    const accessObj = {
      entity_id: id,
      access_from: 'Contact List',
      entity_type: 'CONTACT',
    };
    this.sessionService.createRecentSession(accessObj).subscribe();
    const queryParams = {
      viewContext: ViewContext.contacts,
      ...(this.filteredView ? { filteredView: this.filteredView } : {}),
    };

    this.navigationService.navigateTo('lists/contacts/edit/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  createContact() {
    const queryParams = {
      ...(this.filteredView ? { filteredView: this.filteredView } : {}),
    };
    this.navigationService.navigateTo('lists/contacts/add', queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  addRel() {
    const newRel = new ContactRelationshipModel();
    newRel.clear();
    const dialogRef = this.dialog.open(FilteredViewContactRelationDialogComponent, {
      data: {
        contactEntity: false,
        value: { related: this.allRelated },
      },
      width: '450px',
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.done) {
        newRel.id = res.response.entity_id;
        newRel.entity_id = res.response.entity_id;
        newRel.entity_type = res.response.entity_type;
        newRel.relationship_description = res.response.relationship_description;
        newRel.relation_type = res.response.relation_type;
        newRel.relation_type_id = res.response.relation_type_id;
        newRel.is_prime = res.response.is_prime;
        newRel.emergency_contact = res.response.emergency_contact;
        newRel.financial_responsible = res.response.financial_responsible;
        newRel.phi_authorized = res.response.phi_authorized;
        this.store.dispatch(
          new ContactRelationshipOnServerCreated({
            contactRelationship: newRel,
            // contact: this.contact,
            type: 'contact',
          }),
        );
        const successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.ADD_MESSAGE');
        this.layoutUtilsService.showActionNotification(
          successMsg,
          MessageType.Create,
          3000,
          true,
          false,
        );
      }
    });
  }

  handleSort(event: Event, column: { label: string; prop: string; elementId: any }): void {
    event.stopPropagation();
    if (!column.prop) return;
    if (column.label === this.sortRelation.label) {
      this.sortRelation.type === 'ASC'
        ? (this.sortRelation.type = 'DESC')
        : (this.sortRelation.type = 'ASC');
    } else this.sortRelation = { label: column.label, type: 'ASC' };

    this.loadRelation(this.selectedContact, column.prop, this.sortRelation.type);
  }

  loadRelation(val, direction, active) {
    this.selectedContact = val;
    const queryParams = new QueryParamsModel(
      this.filterConfigurationR(active, direction),
      active !== '' ? active : 'asc',
      direction !== '' && direction !== 'name' ? direction : 'entity.firstName',
      0,
      10,
    );
    this.contactRelationshipsService
      .findContactRelationships(val, '', false, queryParams)
      .subscribe((e) => {
        this.list = e.data;
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  reloadContactsList(): void {
    this.paginator.pageIndex = 0;

    this.loadContactsList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadContactsList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput = '';
    this.searchNameEmailInput = '';
    this.searchUsernameInput = '';
    this.searchCellPhoneInput = '';
    this.searchEntityTypeSelect = [];
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadContactsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadContactsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadContactsList();
  }

  exportContactsAsCSV(event: Event): void {
    event.stopPropagation();

    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'name',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.contactsService
      .getContactsAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        const filename = `Contacts List [${utc(new Date())
          .local()
          .format('YYYY-MM-DD, hh.mm a')}].csv`;
        this.downloadService.downloadFile(res, filename, 'text/csv');
      });
  }

  showContactPhoto(contact) {
    if (!contact.pre_signed_url?.url) {
      return;
    }
    this.dialog.open(ZoomInEntityComponent, { data: { entity: contact }, maxWidth: '600px' });
  }
}
